import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { Toast } from './toast'
import type { ProjectDocument } from '@/types'

export class ProjectDocuments {
  private container: HTMLElement
  private apiService: ApiService
  private appState: AppState
  private toastService: Toast
  private pdfs: ProjectDocument[] = []
  private isLoading = false

  constructor(
    container: HTMLElement,
    apiService: ApiService,
    appState: AppState,
    toastService: Toast
  ) {
    this.container = container
    this.apiService = apiService
    this.appState = appState
    this.toastService = toastService
    
    this.init()
  }

  private init(): void {
    this.setupEventListeners()
    this.loadProjectDocuments()
  }

  private setupEventListeners(): void {
    this.appState.subscribe('currentProject', () => this.loadProjectDocuments())
    this.appState.subscribe('currentFile', () => this.loadProjectDocuments())
    this.appState.subscribe('projectDocuments', (docs: ProjectDocument[]) => {
      this.pdfs = docs
      this.render()
    })
  }

  private async loadProjectDocuments(): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      this.appState.setDocuments([])
      return
    }

    try {
      this.setLoading(true)
      
      // Use new multi-document API
      const result = await this.apiService.getProjectDocuments(currentProject.id)
      this.appState.setDocuments(result.documents || [])
    } catch (error) {
      console.error('Failed to load project documents:', error)
      this.toastService.error('Failed to load project documents', 'Please check your connection and try again')
    } finally {
      this.setLoading(false)
    }
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading
    this.render()
  }

  private async deletePDF(pdf: ProjectDocument): Promise<void> {
    if (!confirm(`Are you sure you want to delete "${pdf.original_filename}"? This will also delete all selections from this PDF.`)) {
      return
    }

    try {
      this.setLoading(true)
      await this.apiService.deletePDF(pdf.id)
      
      // Reload documents from server to ensure consistency
      await this.loadProjectDocuments()
      
      // Clear current file if it was deleted
      const currentFile = this.appState.getCurrentFile()
      if (currentFile?.id === pdf.id) {
        this.appState.setCurrentFile(null)
      }
      
      // Clear selections for this PDF
      const currentSelections = this.appState.getSelections()
      const filteredSelections = currentSelections.filter((s: any) => s.pdf_id !== pdf.id)
      this.appState.setSelections(filteredSelections)
      
      // Refresh project data to update PDF count
      const currentProject = this.appState.getCurrentProject()
      if (currentProject) {
        const updatedProject = await this.apiService.getProject(currentProject.id)
        this.appState.setCurrentProject(updatedProject)
      }
      
      this.toastService.success('PDF Deleted', `"${pdf.original_filename}" was deleted successfully`)
    } catch (error) {
      console.error('Failed to delete PDF:', error)
      this.toastService.error('Failed to delete PDF', 'Please try again')
    } finally {
      this.setLoading(false)
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  private formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString()
  }

  private getDocumentIcon(document: any): string {
    // Check if document has document_type property
    if (document.document_type) {
      switch (document.document_type) {
        case 'image':
          return '🖼️'
        case 'pdf':
          return '📄'
        case 'manual':
          return '📝'
        default:
          return '📄'
      }
    }
    
    // Fallback: check file extension
    const filename = document.original_filename || ''
    if (/\.(jpg|jpeg|png|gif|bmp|tiff|webp)$/i.test(filename)) {
      return '🖼️'
    } else if (/\.(txt)$/i.test(filename)) {
      return '📝'
    } else if (/\.(doc|docx)$/i.test(filename)) {
      return '📋'
    }
    
    return '📄' // Default to PDF icon
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="project-documents">
        ${this.isLoading ? `
          <div class="project-loading">
            <div class="spinner"></div>
            <p>Loading documents...</p>
          </div>
        ` : ''}
        
        ${this.pdfs.length === 0 && !this.isLoading ? `
          <div class="project-empty-state">
            <div class="empty-icon">📄</div>
            <h3>No Documents Yet</h3>
            <p>Upload your first document to get started with text extraction.</p>
          </div>
        ` : ''}
        
        ${this.pdfs.length > 0 ? `
          <div class="document-list">
            <div class="list-header">
              <h3>Project Documents (${this.pdfs.length})</h3>
            </div>
            ${this.pdfs.map(pdf => `
              <div class="document-item" data-pdf-id="${pdf.id}">
                <div class="document-content">
                  <div class="document-header">
                    <div class="document-icon">${this.getDocumentIcon(pdf)}</div>
                    <div class="document-info">
                      <h4 class="document-name">${pdf.original_filename}</h4>
                      <div class="document-meta">
                        <span class="meta-item">${this.formatFileSize(pdf.file_size)}</span>
                        <span class="meta-item">${pdf.page_count || 0} pages</span>
                        <span class="meta-item">${this.formatDate(pdf.upload_timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  <div class="document-actions">
                    <button class="btn btn-sm btn-danger" data-action="delete" data-pdf-id="${pdf.id}" title="Delete PDF">
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `

    this.attachEventListeners()
  }

  private attachEventListeners(): void {
    // Document deletion
    const deleteButtons = this.container.querySelectorAll('[data-action="delete"]')
    deleteButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation()
        const pdfId = button.getAttribute('data-pdf-id')
        const pdf = this.pdfs.find(p => p.id === pdfId)
        if (pdf) {
          this.deletePDF(pdf)
        }
      })
    })
  }

  // Public methods
  public refresh(): void {
    this.loadProjectDocuments()
  }
} 